/* eslint-disable prefer-template */
/**
 * Parse and validate Fixed Budget Vessel Account Code CSV before upload.
 * Backend template headers: SapVesselCompanyCode,AccountCode
 */

const normalizeHeader = (h) => String(h || '')
  .trim()
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const EXPECTED_HEADERS = 'SapVesselCompanyCode,AccountCode';

const SAP_VESSEL_COMPANY_CODE_ALIASES = [
  'sap vessel company code',
  'sapvesselcompanycode',
  'vessel company code',
  'vesselcompanycode'
];

const ACCOUNT_CODE_ALIASES = [
  'account code',
  'accountcode',
  'fixed account code',
  'fixedaccountcode'
];

const HEADER_ALIASES = {
  sapVesselCompanyCode: SAP_VESSEL_COMPANY_CODE_ALIASES,
  accountCode: ACCOUNT_CODE_ALIASES
};

const resolveColumnIndex = (headers) => {
  const map = {
    sapVesselCompanyCode: -1,
    accountCode: -1
  };
  const used = new Set();

  const tryAssign = (key) => {
    const aliases = HEADER_ALIASES[key];
    const collapsedAliases = aliases.map((a) => a.replace(/\s+/g, ''));
    const idx = headers.findIndex((h, i) => {
      if (used.has(i)) return false;
      const normalized = normalizeHeader(h);
      const collapsed = normalized.replace(/\s+/g, '');
      return aliases.includes(normalized) || collapsedAliases.includes(collapsed);
    });
    if (idx >= 0) {
      map[key] = idx;
      used.add(idx);
    }
  };

  tryAssign('accountCode');
  tryAssign('sapVesselCompanyCode');
  return map;
};

const parseCsvLine = (line) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      cells.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
};

const escapeCsvCell = (value) => {
  const str = String(value || '');
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const validateFixedBudgetVesselAccCodeCsv = (fileText) => {
  const errors = [];
  const text = String(fileText || '').replace(/^\uFEFF/, '');
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');

  if (lines.length < 2) {
    return { rows: [], errors: ['No valid data available to upload'], csvBlob: null };
  }

  const headers = parseCsvLine(lines[0]);
  const col = resolveColumnIndex(headers);

  if (col.sapVesselCompanyCode < 0 || col.accountCode < 0) {
    return {
      rows: [],
      errors: [
        'Error in line number 1. CSV must include SapVesselCompanyCode '
        + 'and AccountCode columns.'
      ],
      csvBlob: null
    };
  }

  const rows = [];
  const accountCodeOwners = new Map();

  for (let i = 1; i < lines.length; i += 1) {
    const lineNumber = i + 1;
    const cells = parseCsvLine(lines[i]).map((c) => String(c == null ? '' : c).trim());
    const sapVesselCompanyCode = cells[col.sapVesselCompanyCode] || '';
    const accountCode = cells[col.accountCode] || '';

    if (!sapVesselCompanyCode) {
      errors.push(
        'Error in line number ' + lineNumber
        + '. The sap vessel company code is either missing or incorrect.'
      );
    }
    if (!accountCode) {
      errors.push(
        'Error in line number ' + lineNumber
        + '. The account code is either missing or incorrect.'
      );
    }

    if (sapVesselCompanyCode && accountCode) {
      const existingOwner = accountCodeOwners.get(accountCode);
      const duplicateAccountCodeOwner = accountCodeOwners.has(accountCode)
        && existingOwner !== sapVesselCompanyCode;
      if (duplicateAccountCodeOwner) {
        errors.push(
          'Error in line number ' + lineNumber
          + '. The same account code is added to different vessels.'
        );
      } else {
        accountCodeOwners.set(accountCode, sapVesselCompanyCode);
      }
      rows.push({ sapVesselCompanyCode, accountCode, lineNumber });
    }
  }

  if (rows.length === 0 && errors.length === 0) {
    errors.push('No valid data available to upload');
  }

  if (errors.length > 0) {
    return { rows: [], errors, csvBlob: null };
  }

  const csvContent = [
    EXPECTED_HEADERS,
    ...rows.map(
      (r) => [
        escapeCsvCell(r.sapVesselCompanyCode),
        escapeCsvCell(r.accountCode)
      ].join(',')
    )
  ].join('\r\n');

  const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  return { rows, errors: [], csvBlob };
};

export { validateFixedBudgetVesselAccCodeCsv, EXPECTED_HEADERS };
